import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';

import { applySeo, resetSeo } from '../lib/seo';
import { fetchListingById, fetchListingBySlug, fetchReviews, createReview } from '../lib/api';
import { seedReviews } from '../data/seed';
import SmartImage from '../components/SmartImage';
import JsonLd from '../components/JsonLd';
import type { Listing, Review } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/.netlify/functions';

function makeSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-');
}

export default function ListingDetail() {
  const { categorySlug, listingSlug, id } = useParams<{ categorySlug?: string; listingSlug?: string; id?: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState({ name: '', email: '', message: '' });
  const [claimStatus, setClaimStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [reviewForm, setReviewForm] = useState({ reviewer_name: '', rating: 5, text: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const listingId = listing?.id || 0;

  // Dynamic SEO meta tags per listing
  useEffect(() => {
    if (listing) {
      const services = listing.services?.join(', ') || '';
      const region = listing.region_name || 'New Zealand';
      const catSlug = listing.category_slug || listing.category_id;
      const slug = listing.slug || makeSlug(listing.title);
      applySeo({
        title: `${listing.title} - ${region} | MarineLink`,
        description: `${listing.title} - ${listing.category_name} in ${region}. ${services}. ${listing.description || ''}`,
        descriptionPad: `Browse ${listing.category_name} listings and more marine businesses across New Zealand on MarineLink, the NZ marine industry directory. Contact businesses directly.`,
        path: `/listing/${catSlug}/${slug}`,
        image: listing.image_url || undefined,
        keywords: `${listing.title}, ${listing.category_name}, ${services}, New Zealand marine, ${region}`,
      });
    }
    return () => resetSeo();
  }, [listing]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    if (listingSlug) {
      // New SEO-friendly URL: /listing/:categorySlug/:listingSlug
    fetchListingBySlug(listingSlug).then((l) => {
      setListing(l);
      setLoading(false);
      if (l) {
        fetchReviews(l.id).then(setReviews);
        // Fetch gallery images if available
        fetch(`${API_BASE}/listing-images?listing_id=${l.id}`)
          .then(r => r.ok ? r.json() : [])
          .then((imgs: { url: string }[]) => {
            const urls = imgs.map(i => i.url).filter(Boolean);
            setGalleryImages(urls);
          })
          .catch(() => setGalleryImages([]));
      }
    });
    } else if (id) {
      // Legacy URL: /listing/:id - numeric id OR bare slug (dispatch inside fetchListingById)
      fetchListingById(id).then((l) => {
        setListing(l);
        setLoading(false);
        if (l) {
          fetchReviews(l.id).then(setReviews);
        }
      });
    } else {
      setListing(null);
      setLoading(false);
    }
  }, [categorySlug, listingSlug, id]);

  // Redirect old numeric URLs to new SEO URLs
  if (listing && id && !listingSlug) {
    const catSlug = listing.category_slug || listing.category_id;
    const lSlug = listing.slug || makeSlug(listing.title);
    return <Navigate to={`/listing/${catSlug}/${lSlug}`} replace />;
  }

  if (loading) {
    return <div className="text-center py-24 text-text-muted">Loading...</div>;
  }

  if (!listing) {
    return (
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-24 text-center text-text-muted">
        <h3 className="text-lg text-text-secondary mb-3">Listing not found</h3>
        <p>This listing may have been removed.</p>
        <Link to="/browse" className="btn btn-ghost mt-4">Browse all listings</Link>
      </div>
    );
  }

  const badgeClass = listing.badge === 'featured'
    ? 'bg-accent text-bg'
    : listing.badge === 'new'
    ? 'bg-green text-white'
    : listing.badge === 'premium'
    ? 'bg-amber text-bg'
    : '';
  const badgeText = listing.badge === 'featured' ? 'Featured'
    : listing.badge === 'new' ? 'New'
    : listing.badge === 'premium' ? 'Premium'
    : '';
 const img = listing.image_url || 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&q=80';
  const allImages = galleryImages.length > 0 ? galleryImages : [img];
  const activeImage = allImages[activeImageIdx] || img;

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await createReview({
      listing_id: listingId,
      reviewer_name: reviewForm.reviewer_name,
      rating: reviewForm.rating,
      text: reviewForm.text,
    });
    if (result.success) {
      setReviewSubmitted(true);
      setReviews([...reviews, {
        id: Date.now(),
        listing_id: listingId,
        reviewer_name: reviewForm.reviewer_name,
        rating: reviewForm.rating,
        text: reviewForm.text,
      }]);
      setReviewForm({ reviewer_name: '', rating: 5, text: '' });
      setShowReviewForm(false);
    }
  };

  const handleClaimSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setClaimStatus('sending');
    try {
      const response = await fetch(`${API_BASE}/claim-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: listingId, ...claimForm }),
      });
      if (!response.ok) throw new Error('Claim request failed');
      setClaimStatus('sent');
      fetch(`${API_BASE}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'claim_request_submitted', properties: { listing_id: String(listingId) } }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      setClaimStatus('error');
    }
  };

  // AEO: structured data for answer engines and rich results
  const listingUrl = `https://marinelink.co.nz/listing/${listing.category_slug || listing.category_id}/${listing.slug || makeSlug(listing.title)}`;
  const listingSchema = {
    '@context': 'https://schema.org',
    '@type': listing.category_id === 'ramps' ? 'Place' : 'LocalBusiness',
    name: listing.title,
    description: (listing.description || '').slice(0, 300),
    url: listingUrl,
    ...(listing.image_url ? { image: listing.image_url.startsWith('http') ? listing.image_url : `https://marinelink.co.nz${listing.image_url}` } : {}),
    ...(listing.phone ? { telephone: listing.phone } : {}),
    ...(listing.lat && listing.lng ? { geo: { '@type': 'GeoCoordinates', latitude: listing.lat, longitude: listing.lng } } : {}),
    address: { '@type': 'PostalAddress', addressRegion: listing.region_name || '', addressCountry: 'NZ' },
    ...(listing.rating > 0 && listing.reviews_count > 0 ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: listing.rating, reviewCount: listing.reviews_count } } : {}),
    isPartOf: { '@type': 'WebSite', '@id': 'https://marinelink.co.nz/#website' },
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-4 md:py-12 pb-24">
      <JsonLd id={`listing-${listing.id}`} data={listingSchema} />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4 flex-wrap">
        <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <Link to={`/browse?category=${listing.category_id}`} className="text-text-secondary hover:text-accent transition-colors">
          {listing.category_name}
        </Link>
        <span>/</span>
        <span>{listing.title}</span>
      </div>

      {/* Hero image - adaptive based on aspect ratio */}
      <div className="relative h-[180px] sm:h-[240px] md:h-[320px] rounded-xl overflow-hidden mb-3 bg-elevated-light dark:bg-elevated">
        <SmartImage
          src={activeImage}
          alt={listing.title}
          fallback="https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1200&q=80"
          mode="adaptive"
          className="w-full h-full"
        />
        {badgeText && (
          <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${badgeClass}`}>
            {badgeText}
          </span>
        )}
      </div>
      {/* Gallery thumbnails */}
      {allImages.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {allImages.map((gImg, idx) => (
            <button
              key={idx}
              onClick={() => setActiveImageIdx(idx)}
              className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors ${idx === activeImageIdx ? 'border-accent' : 'border-transparent opacity-70 hover:opacity-100'}`}
            >
              <SmartImage
                src={gImg}
                alt={`${listing.title} image ${idx + 1}`}
                mode="cover"
                className="w-full h-full"
              />
            </button>
          ))}
        </div>
      )}
      {allImages.length <= 1 && <div className="mb-6"></div>}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-6 md:gap-12">
        {/* Main */}
        <div>
          <div className="text-xs text-accent font-semibold uppercase tracking-wide mb-2">{listing.category_name}</div>
          <h1 className="font-display text-xl md:text-[1.8rem] font-bold mb-3">{listing.title}</h1>
          <div className="flex items-center gap-2 text-text-secondary text-sm mb-6 flex-wrap">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {listing.region_name}, New Zealand
            {listing.rating > 0 && (
              <span className="ml-4">★ {listing.rating} ({listing.reviews_count} reviews)</span>
            )}
          </div>

          <div className="text-black dark:text-text-primary leading-relaxed mb-6">
            {(() => {
              const desc = listing.description || '';
              // If description has explicit newlines, split on those
              // Otherwise, split long descriptions into paragraphs by sentence boundaries (every 2-3 sentences)
              let paragraphs: string[];
              if (desc.includes('\n')) {
                paragraphs = desc.split(/\n+/).filter(p => p.trim().length > 0);
              } else if (desc.length > 400) {
                // Split on sentence boundaries: . ! ? followed by space + capital
                const sentences = desc.match(/[^.!?]+[.!?]+\s*/g) || [desc];
                paragraphs = [];
                for (let i = 0; i < sentences.length; i += 2) {
                  const chunk = (sentences[i] || '') + (sentences[i + 1] || '');
                  if (chunk.trim()) paragraphs.push(chunk.trim());
                }
                // If we only got one paragraph from splitting, and it's very long, try 3-sentence chunks
                if (paragraphs.length === 1 && desc.length > 800) {
                  paragraphs = [];
                  for (let i = 0; i < sentences.length; i += 3) {
                    const chunk = (sentences[i] || '') + (sentences[i + 1] || '') + (sentences[i + 2] || '');
                    if (chunk.trim()) paragraphs.push(chunk.trim());
                  }
                }
              } else {
                paragraphs = [desc];
              }
              return paragraphs.map((p, i) => (
                <p key={i} className={i > 0 ? 'mt-4' : ''}>{p}</p>
              ));
            })()}
            {listing.services.length > 0 && (
              <>
                <h3 className="text-text-primary-light dark:text-text-primary text-lg font-semibold mb-3 mt-5">Services offered</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {listing.services.map((s, i) => (
                    <span key={i} className="px-3 py-1 bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-full text-sm text-text-secondary">
                      {s}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Reviews */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-text-primary-light dark:text-text-primary text-lg font-semibold">Reviews ({reviews.length})</h3>
              {!reviewSubmitted && (
                <button onClick={() => setShowReviewForm(!showReviewForm)} className="btn btn-ghost btn-sm">
                  {showReviewForm ? 'Cancel' : 'Write a review'}
                </button>
              )}
            </div>

            {showReviewForm && (
              <form onSubmit={handleReviewSubmit} className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-lg p-5 mb-4">
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-text-secondary mb-2">Your name</label>
                  <input
                    type="text"
                    required
                    value={reviewForm.reviewer_name}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                    className="form-input"
                    placeholder="John D."
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-text-secondary mb-2">Rating</label>
                  <select
                    value={reviewForm.rating}
                    onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })}
                    className="form-select"
                  >
                    <option value={5}>★★★★★ Excellent</option>
                    <option value={4}>★★★★ Good</option>
                    <option value={3}>★★★ Average</option>
                    <option value={2}>★★ Poor</option>
                    <option value={1}>★ Bad</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-text-secondary mb-2">Review</label>
                  <textarea
                    required
                    value={reviewForm.text}
                    onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
                    className="form-textarea"
                    placeholder="Share your experience..."
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-sm">Submit review</button>
              </form>
            )}

            {reviews.length === 0 ? (
              <p className="text-text-muted text-sm">No reviews yet. Be the first to review!</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-lg p-5 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-accent-deep flex items-center justify-center font-semibold text-white text-sm">
                      {r.reviewer_name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{r.reviewer_name}</div>
                    </div>
                    <div className="ml-auto text-amber">
                      {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                    </div>
                  </div>
                  <div className="text-text-secondary text-sm leading-relaxed">{r.text}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-4">
          <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-lg p-5">
            <h4 className="text-xs uppercase tracking-wider text-text-muted mb-3">Contact</h4>
            {listing.phone && (
              <div className="flex items-center gap-3 py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-accent flex-shrink-0">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto">{listing.phone}</span>
              </div>
            )}
            {listing.email && (
              <div className="flex items-center gap-3 py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-accent flex-shrink-0">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto text-right">{listing.email}</span>
              </div>
            )}
            {listing.website && (
              <div className="flex items-center gap-3 py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-accent flex-shrink-0">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <a href={listing.website} target="_blank" rel="noopener" className="text-accent ml-auto text-right">Visit website →</a>
              </div>
            )}
            {listing.email && (
              <a href={`mailto:${listing.email}`} className="btn btn-primary btn-block btn-sm mt-4" onClick={() => {
                fetch(`${API_BASE}/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: 'enquiry_clicked', properties: { listing_id: String(listingId) } }), keepalive: true }).catch(() => undefined);
              }}>Send enquiry</a>
            )}
            <button type="button" className="btn btn-ghost btn-block btn-sm mt-2" onClick={() => setShowClaimForm(!showClaimForm)}>
              {showClaimForm ? 'Close claim request' : 'Own this business? Claim this listing'}
            </button>
            {showClaimForm && (
              <div className="mt-4 pt-4 border-t border-[rgba(16,42,66,0.12)]">
                {claimStatus === 'sent' ? (
                  <p className="text-green text-sm">Thanks - we’ve received your claim request and will be in touch.</p>
                ) : (
                  <form onSubmit={handleClaimSubmit} className="flex flex-col gap-3">
                    <p className="text-text-secondary text-xs">Tell us who you are and we’ll help verify and manage this listing.</p>
                    <input className="form-input" required placeholder="Your name" value={claimForm.name} onChange={(e) => setClaimForm({ ...claimForm, name: e.target.value })} />
                    <input className="form-input" required type="email" placeholder="Business email" value={claimForm.email} onChange={(e) => setClaimForm({ ...claimForm, email: e.target.value })} />
                    <textarea className="form-textarea" placeholder="Anything we should know? (optional)" value={claimForm.message} onChange={(e) => setClaimForm({ ...claimForm, message: e.target.value })} />
                    {claimStatus === 'error' && <p className="text-red text-xs">Couldn’t submit that request. Please try again.</p>}
                    <button type="submit" disabled={claimStatus === 'sending'} className="btn btn-primary btn-sm">{claimStatus === 'sending' ? 'Sending...' : 'Request ownership'}</button>
                  </form>
                )}
              </div>
            )}
          </div>

          {listing.lat != null && listing.lng != null && (
            <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-lg p-5">
              <h4 className="text-xs uppercase tracking-wider text-text-muted mb-3">Location</h4>
              <iframe
                src={`https://www.google.com/maps?q=${listing.lat},${listing.lng}&z=15&output=embed`}
                className="w-full h-[190px] rounded-lg border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Map of ${listing.title}`}
              />
              <a
                href={listing.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${listing.lat},${listing.lng}`}
                target="_blank"
                rel="noopener"
                className="text-accent text-sm inline-block mt-3"
              >
                Open in Google Maps →
              </a>
            </div>
          )}

          <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-lg p-5">
            <h4 className="text-xs uppercase tracking-wider text-text-muted mb-3">Details</h4>
            <div className="flex items-center py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
              <span className="text-text-muted">Category</span>
              <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto">{listing.category_name}</span>
            </div>
            <div className="flex items-center py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
              <span className="text-text-muted">Region</span>
              <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto">{listing.region_name}</span>
            </div>
            {listing.rating > 0 && (
              <div className="flex items-center py-2 border-b border-[rgba(16,42,66,0.12)] text-sm">
                <span className="text-text-muted">Rating</span>
                <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto">★ {listing.rating}</span>
              </div>
            )}
            <div className="flex items-center py-2 text-sm">
              <span className="text-text-muted">Reviews</span>
              <span className="text-text-primary-light dark:text-text-primary font-medium ml-auto">{listing.reviews_count}</span>
            </div>
            {listing.badge && (
              <div className="flex items-center py-2 text-sm">
                <span className="text-text-muted">Status</span>
                <span className="text-accent font-medium ml-auto capitalize">{listing.badge}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
